import { useEffect, useId, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useToast } from '../../components/ui/Toast';
import {
  useCampaign,
  useCreateCampaign,
  useUpdateCampaign,
  type CampaignType,
} from '../../api/campaigns.api';
import { useCampaignTemplates, type CampaignTemplate } from '../../api/campaign-templates.api';
import { useOrgSettings } from '../../api/org-settings.api';
import { availableToOrg } from '../../lib/org-type';
import {
  FormCard,
  FormHeader,
  WizardLayout,
  WizardFooter,
  enrollmentMode,
  stepInfo,
  stepPath,
} from './builder-shell';

/* ── Screen 13 — Campaign builder · Step 1 Basics (wired) ── */

// Campaign types split into two categories so the UI never conflates the two:
//   - 'outreach'  → one-to-one engagement, sent from a recruiter mailbox
//   - 'broadcast' → bulk newsletter, sent to an opted-in marketing list via a
//                   verified sending domain. Visually separated below.
const TYPES: {
  id: CampaignType;
  label: string;
  description: string;
  icon: ReactNode;
  category: 'outreach' | 'broadcast';
}[] = [
  { id: 'CANDIDATE', label: 'Candidate', description: 'Reach passive talent for an open role', icon: <IconUsers />, category: 'outreach' },
  { id: 'BD', label: 'BD', description: 'Pitch new business prospects', icon: <IconBriefcase />, category: 'outreach' },
  { id: 'JOB', label: 'Job', description: 'Promote a specific live role', icon: <IconMegaphone />, category: 'outreach' },
  { id: 'DEAL', label: 'Deal', description: 'Nurture pipeline opportunities', icon: <IconHandshake />, category: 'outreach' },
  { id: 'EVENT', label: 'Event', description: 'Drive registrations for an event', icon: <IconCalendar />, category: 'outreach' },
  { id: 'NEWSLETTER', label: 'Newsletter', description: 'Bulk email to an opted-in marketing list via your verified sending domain', icon: <IconNewspaper />, category: 'broadcast' },
];

const NAME_MAX = 120;
const DESC_MAX = 280;

export function CampaignBuilderPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();

  const isNew = !id;
  const { data: existing, isLoading: loadingExisting } = useCampaign(id);

  const [name, setName] = useState('');
  const [type, setType] = useState<CampaignType>('CANDIDATE');
  const [description, setDescription] = useState('');
  const [touched, setTouched] = useState(false);
  // template_id is only meaningful on create — it tells the API to seed
  // the new campaign's sequence + trigger config from a saved template.
  // Reset on success (handled in persist()).
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);

  const createMut = useCreateCampaign();
  const updateMut = useUpdateCampaign(id ?? '');

  useEffect(() => {
    if (existing) {
      setName(existing.name);
      setType(existing.type);
      setDescription(existing.description ?? '');
    }
  }, [existing]);

  const nameError = touched && name.trim().length === 0 ? 'Campaign name is required' : null;
  const isValid = name.trim().length > 0 && name.length <= NAME_MAX && description.length <= DESC_MAX;
  const isPending = createMut.isPending || updateMut.isPending;

  const persist = async (): Promise<string | null> => {
    const trimmedName = name.trim();
    const trimmedDesc = description.trim();
    try {
      if (isNew) {
        const created = await createMut.mutateAsync({
          name: trimmedName,
          type,
          description: trimmedDesc || null,
          // template_id is consumed once on create and never sent again —
          // the persisted campaign is independent of the template thereafter.
          ...(templateId ? { template_id: templateId } : {}),
        });
        return created.id;
      } else if (id) {
        await updateMut.mutateAsync({
          name: trimmedName,
          type,
          description: trimmedDesc || null,
        });
        return id;
      }
      return null;
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error ?? 'Could not save campaign';
      addToast({ variant: 'danger', title: 'Save failed', description: msg });
      return null;
    }
  };

  const handleTemplatePicked = (tpl: CampaignTemplate) => {
    // Pre-fill the basics form from the template. The user can still edit
    // any of these before clicking Next — the template_id stays attached
    // until the campaign is committed so the API can seed the sequence.
    setTemplateId(tpl.id);
    setType(tpl.type);
    // Don't overwrite a name the user has already typed.
    if (!name.trim()) setName(`${tpl.name} copy`);
    if (!description.trim() && tpl.description) setDescription(tpl.description);
    setTemplatePickerOpen(false);
    addToast({
      variant: 'success',
      title: 'Template loaded',
      description: `${tpl.sequence_steps.length}-step sequence will copy when you save.`,
    });
  };

  const clearTemplate = () => setTemplateId(null);

  const onSaveDraft = async () => {
    setTouched(true);
    if (!isValid) return;
    const saved = await persist();
    if (saved) {
      addToast({ variant: 'success', title: 'Draft saved' });
      if (isNew) navigate(`/campaigns/${saved}/edit`, { replace: true });
    }
  };

  const onNext = async () => {
    setTouched(true);
    if (!isValid) return;
    const saved = await persist();
    if (saved) navigate(stepPath(saved, 'triggers'));
  };

  if (loadingExisting && !isNew) {
    return (
      <WizardLayout step="basics">
        <FormCard>
          <p style={{ fontFamily: 'var(--font-sans)', color: 'var(--color-text-muted)' }}>Loading campaign…</p>
        </FormCard>
      </WizardLayout>
    );
  }

  const info = stepInfo('basics', enrollmentMode(existing));

  return (
    <WizardLayout step="basics" campaignId={id} campaign={existing ?? null}>
      <FormCard>
        <FormHeader stepIndex={info.index} totalSteps={info.total} label="Basics" />

        <p
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-md)',
            color: 'var(--color-text-secondary)',
            lineHeight: 'var(--leading-relaxed)',
          }}
        >
          Give your campaign a clear internal name and tell us what kind of outreach this is. You can change these later before launching.
        </p>

        {isNew && (
          <LoadTemplateRow
            templateId={templateId}
            onOpen={() => setTemplatePickerOpen(true)}
            onClear={clearTemplate}
          />
        )}

        <CampaignNameField value={name} onChange={setName} max={NAME_MAX} error={nameError} />
        <TypeSelector selected={type} onSelect={setType} />
        <DescriptionField value={description} onChange={setDescription} max={DESC_MAX} />

        <SaveDraftRow onSaveDraft={onSaveDraft} isPending={isPending} disabled={!isValid} />

        <WizardFooter
          onNext={onNext}
          nextLabel="Next: Enrollment"
          isPending={isPending}
          primaryDisabled={!isValid}
          helper={
            <LinkiTip>
              Linki can suggest a starter sequence based on this {TYPES.find((t) => t.id === type)?.label.toLowerCase()} type.
            </LinkiTip>
          }
        />
      </FormCard>

      {templatePickerOpen && (
        <TemplatePickerModal
          onPick={handleTemplatePicked}
          onClose={() => setTemplatePickerOpen(false)}
        />
      )}
    </WizardLayout>
  );
}

/* ── Load template UI ── */

function LoadTemplateRow({
  templateId,
  onOpen,
  onClear,
}: {
  templateId: string | null;
  onOpen: () => void;
  onClear: () => void;
}) {
  const isLoaded = !!templateId;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 'var(--space-3)',
        padding: 'var(--space-3) var(--space-4)',
        borderRadius: 'var(--radius-input)',
        backgroundColor: isLoaded ? 'var(--color-success-bg, #e8f7ed)' : 'var(--color-surface-muted)',
        border: `1px solid ${isLoaded ? 'var(--color-success-border, #a8d8b9)' : 'var(--color-border-default)'}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span
          aria-hidden="true"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '28px',
            height: '28px',
            borderRadius: 'var(--radius-full)',
            backgroundColor: 'var(--color-surface-white)',
            color: isLoaded ? 'var(--color-success)' : 'var(--color-text-muted)',
          }}
        >
          <IconBookmark size={14} />
        </span>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
            {isLoaded ? 'Template loaded' : 'Start from a saved template?'}
          </span>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
            {isLoaded
              ? 'The sequence and trigger from the template will copy in when you save.'
              : 'Skip the sequence build: pick a campaign template your team already shaped up.'}
          </span>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        {isLoaded && (
          <button
            type="button"
            onClick={onClear}
            style={{
              height: 'var(--btn-height-sm)',
              padding: '0 var(--btn-padding-x-sm)',
              borderRadius: 'var(--btn-radius)',
              backgroundColor: 'transparent',
              color: 'var(--color-text-muted)',
              border: 'none',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--text-xs)',
              fontWeight: 'var(--font-weight-semibold)',
              cursor: 'pointer',
            }}
          >
            Clear
          </button>
        )}
        <button
          type="button"
          onClick={onOpen}
          style={{
            height: 'var(--btn-height-sm)',
            padding: '0 var(--btn-padding-x-sm)',
            borderRadius: 'var(--btn-radius)',
            backgroundColor: 'var(--color-action-secondary)',
            color: 'var(--color-action-secondary-text)',
            border: '1px solid var(--color-action-secondary-border)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-xs)',
            fontWeight: 'var(--font-weight-semibold)',
            cursor: 'pointer',
          }}
        >
          {isLoaded ? 'Change template' : 'Load template'}
        </button>
      </div>
    </div>
  );
}

function TemplatePickerModal({ onPick, onClose }: { onPick: (t: CampaignTemplate) => void; onClose: () => void }) {
  const { data, isLoading } = useCampaignTemplates();
  const jtOrgType = useOrgSettings().data?.data?.jt_org_type;
  const templates = (data?.data ?? []).filter((t) => availableToOrg(t.type, jtOrgType));

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="tpl-picker-title"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 'var(--z-modal)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'var(--modal-overlay-bg)',
        padding: 'var(--space-4)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 560,
          maxHeight: '80vh',
          backgroundColor: 'var(--card-bg)',
          borderRadius: 'var(--card-radius)',
          boxShadow: '0 24px 48px rgba(0,0,0,0.12)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <header style={{ padding: 'var(--space-5) var(--space-6)', borderBottom: '1px solid var(--color-border-default)' }}>
          <h2 id="tpl-picker-title" style={{ margin: 0, fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xl)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-brand)' }}>
            Load a campaign template
          </h2>
          <p style={{ marginTop: '2px', fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
            Picking one will pre-fill the basics and copy its sequence into your new campaign. You can still edit everything before launching.
          </p>
        </header>

        <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--space-3) var(--space-6) var(--space-5)' }}>
          {isLoading && (
            <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', padding: 'var(--space-4)' }}>
              Loading templates…
            </p>
          )}
          {!isLoading && templates.length === 0 && (
            <div
              style={{
                padding: 'var(--space-5)',
                borderRadius: 'var(--radius-input)',
                border: '1px dashed var(--color-border-strong)',
                textAlign: 'center',
                fontFamily: 'var(--font-sans)',
                fontSize: 'var(--text-sm)',
                color: 'var(--color-text-muted)',
              }}
            >
              No saved templates yet. Save a campaign as a template from its detail page first.
            </div>
          )}
          {templates.length > 0 && (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              {templates.map((t) => (
                <li key={t.id}>
                  <button
                    type="button"
                    onClick={() => onPick(t)}
                    style={{
                      width: '100%',
                      textAlign: 'left',
                      padding: 'var(--space-3) var(--space-4)',
                      borderRadius: 'var(--radius-input)',
                      border: '1px solid var(--color-border-default)',
                      backgroundColor: 'var(--color-surface-white)',
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                      fontFamily: 'var(--font-sans)',
                      transition: 'border-color var(--transition-base), background-color var(--transition-base)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-action-primary)';
                      e.currentTarget.style.backgroundColor = 'var(--color-blue-50)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = 'var(--color-border-default)';
                      e.currentTarget.style.backgroundColor = 'var(--color-surface-white)';
                    }}
                  >
                    <span style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--color-text-primary)' }}>
                      {t.name}
                    </span>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
                      {t.type.toLowerCase()} · {t.sequence_steps.length} step{t.sequence_steps.length === 1 ? '' : 's'}
                      {t.trigger_config ? ' · auto-enrollment trigger' : ''}
                    </span>
                    {t.description && (
                      <span style={{ marginTop: '4px', fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)' }}>
                        {t.description}
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)', padding: 'var(--space-4) var(--space-6)', borderTop: '1px solid var(--color-border-default)' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              height: 'var(--btn-height)',
              padding: '0 var(--btn-padding-x)',
              borderRadius: 'var(--btn-radius)',
              backgroundColor: 'var(--color-action-secondary)',
              color: 'var(--color-action-secondary-text)',
              border: '1px solid var(--color-action-secondary-border)',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--btn-font-size)',
              fontWeight: 'var(--btn-font-weight)',
              cursor: 'pointer',
            }}
          >
            Close
          </button>
        </footer>
      </div>
    </div>
  );
}

function IconBookmark({ size = 14 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z" />
    </svg>
  );
}

/* ── Save draft helper row ── */

function SaveDraftRow({ onSaveDraft, isPending, disabled }: { onSaveDraft: () => void; isPending: boolean; disabled: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
      <button
        type="button"
        onClick={onSaveDraft}
        disabled={disabled || isPending}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--space-1)',
          height: 'var(--btn-height-sm)',
          padding: '0 var(--btn-padding-x-sm)',
          borderRadius: 'var(--btn-radius)',
          backgroundColor: 'transparent',
          color: 'var(--color-text-link)',
          border: 'none',
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          cursor: disabled || isPending ? 'not-allowed' : 'pointer',
          opacity: disabled || isPending ? 0.5 : 1,
        }}
      >
        <IconSave size={14} />
        {isPending ? 'Saving…' : 'Save draft'}
      </button>
    </div>
  );
}

/* ── Fields ── */

function CampaignNameField({ value, onChange, max, error }: { value: string; onChange: (v: string) => void; max: number; error: string | null }) {
  const id = useId();
  return (
    <FieldShell
      id={id}
      label="Campaign name"
      required
      counter={`${value.length}/${max}`}
      counterTone={value.length > max ? 'danger' : 'muted'}
      hint="Internal only: not shown to candidates. Tip: include the role, location, and intent."
      error={error}
    >
      <input
        id={id}
        type="text"
        value={value}
        maxLength={max}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Senior Backend Engineers · Sydney · Q3 nudge"
        style={inputStyle()}
        onFocus={(e) => focusInput(e.currentTarget)}
        onBlur={(e) => blurInput(e.currentTarget)}
      />
    </FieldShell>
  );
}

function TypeSelector({ selected, onSelect }: { selected: CampaignType; onSelect: (t: CampaignType) => void }) {
  const jtOrgType = useOrgSettings().data?.data?.jt_org_type;
  // An already-saved type stays visible so the selection never disappears.
  const offered = TYPES.filter((t) => t.id === selected || availableToOrg(t.id, jtOrgType));
  const outreachTypes = offered.filter((t) => t.category === 'outreach');
  const broadcastTypes = offered.filter((t) => t.category === 'broadcast');

  return (
    <fieldset style={{ border: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <legend
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 'var(--text-sm)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--color-text-secondary)',
          padding: 0,
        }}
      >
        Campaign type
        <span aria-hidden="true" style={{ color: 'var(--color-text-danger)', marginLeft: 'var(--space-1)' }}>
          *
        </span>
      </legend>

      {/* One-to-one engagement — sent from a recruiter mailbox. */}
      <TypeGroup
        title="One-to-one engagement"
        hint="Personal outreach sent from a connected mailbox to individuals."
        types={outreachTypes}
        selected={selected}
        onSelect={onSelect}
      />

      {/* Bulk / marketing — deliberately separated so it never reads as the
          same thing as one-to-one outreach. */}
      <TypeGroup
        title="Bulk / marketing"
        hint="A different kind of send: one email to a whole opted-in list, via your verified marketing domain, not personal outreach."
        types={broadcastTypes}
        selected={selected}
        onSelect={onSelect}
        accent
      />
    </fieldset>
  );
}

function TypeGroup({
  title,
  hint,
  types,
  selected,
  onSelect,
  accent,
}: {
  title: string;
  hint: string;
  types: typeof TYPES;
  selected: CampaignType;
  onSelect: (t: CampaignType) => void;
  accent?: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-2)',
        // The broadcast group gets a subtle bounding box so it reads as a
        // distinct section from the one-to-one cards above it.
        ...(accent
          ? {
              padding: 'var(--space-3)',
              borderRadius: 'var(--radius-input)',
              backgroundColor: 'var(--color-surface-muted)',
              border: '1px dashed var(--color-border-strong)',
            }
          : {}),
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-bold)', color: 'var(--color-text-secondary)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)' }}>
          {title}
        </span>
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>{hint}</span>
      </div>

      <div role="radiogroup" aria-label={title} style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 'var(--space-3)' }}>
        {types.map((t) => {
          const isSelected = t.id === selected;
          return (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              onClick={() => onSelect(t.id)}
              style={{
                textAlign: 'left',
                padding: 'var(--space-4)',
                borderRadius: 'var(--radius-input)',
                border: `1.5px solid ${isSelected ? 'var(--color-action-primary)' : 'var(--color-border-default)'}`,
                backgroundColor: isSelected ? 'var(--color-blue-50)' : 'var(--color-surface-white)',
                boxShadow: isSelected ? 'var(--shadow-focus)' : 'none',
                cursor: 'pointer',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--space-2)',
                fontFamily: 'var(--font-sans)',
                transition: 'border-color var(--transition-base), background-color var(--transition-base), box-shadow var(--transition-base)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 'var(--space-9)',
                    height: 'var(--space-9)',
                    borderRadius: 'var(--radius-input)',
                    backgroundColor: isSelected ? 'var(--color-action-primary)' : 'var(--color-surface-muted)',
                    color: isSelected ? 'var(--color-action-primary-text)' : 'var(--color-text-secondary)',
                  }}
                >
                  {t.icon}
                </span>
                {isSelected && (
                  <span
                    aria-hidden="true"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '20px',
                      height: '20px',
                      borderRadius: 'var(--radius-full)',
                      backgroundColor: 'var(--color-action-primary)',
                      color: 'var(--color-action-primary-text)',
                    }}
                  >
                    <IconCheck size={12} />
                  </span>
                )}
              </div>
              <span style={{ fontSize: 'var(--text-md)', fontWeight: 'var(--font-weight-semibold)', color: isSelected ? 'var(--color-text-brand)' : 'var(--color-text-primary)', letterSpacing: 'var(--tracking-tight)' }}>
                {t.label}
              </span>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', lineHeight: 'var(--leading-snug)' }}>{t.description}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DescriptionField({ value, onChange, max }: { value: string; onChange: (v: string) => void; max: number }) {
  const id = useId();
  return (
    <FieldShell
      id={id}
      label="Description"
      optional
      counter={`${value.length}/${max}`}
      counterTone={value.length > max ? 'danger' : 'muted'}
      hint="Optional context for teammates: what is this campaign trying to achieve?"
    >
      <textarea
        id={id}
        value={value}
        maxLength={max}
        rows={4}
        onChange={(e) => onChange(e.target.value)}
        style={{
          ...inputStyle(),
          height: 'auto',
          minHeight: 'calc(var(--input-height) * 2.4)',
          paddingTop: 'var(--space-3)',
          paddingBottom: 'var(--space-3)',
          resize: 'vertical',
          lineHeight: 'var(--leading-normal)',
        }}
        onFocus={(e) => focusInput(e.currentTarget)}
        onBlur={(e) => blurInput(e.currentTarget)}
      />
    </FieldShell>
  );
}

/* ── Field shell + helpers ── */

function FieldShell({
  id,
  label,
  required,
  optional,
  counter,
  counterTone = 'muted',
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  optional?: boolean;
  counter?: string;
  counterTone?: 'muted' | 'danger';
  hint?: string;
  error?: string | null;
  children: ReactNode;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-1)' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 'var(--space-2)' }}>
        <label
          htmlFor={id}
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-semibold)',
            color: 'var(--color-text-secondary)',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 'var(--space-1)',
          }}
        >
          {label}
          {required && <span aria-hidden="true" style={{ color: 'var(--color-text-danger)' }}>*</span>}
          {optional && <span style={{ fontSize: 'var(--text-xs)', fontWeight: 'var(--font-weight-regular)', color: 'var(--color-text-subtle)', marginLeft: 'var(--space-1)' }}>· optional</span>}
        </label>
        {counter && (
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-xs)', color: counterTone === 'danger' ? 'var(--color-text-danger)' : 'var(--color-text-muted)' }}>
            {counter}
          </span>
        )}
      </div>
      {children}
      {error ? (
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-danger)', lineHeight: 'var(--leading-normal)' }}>{error}</p>
      ) : hint ? (
        <p style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', lineHeight: 'var(--leading-normal)' }}>{hint}</p>
      ) : null}
    </div>
  );
}

function inputStyle(): React.CSSProperties {
  return {
    display: 'block',
    width: '100%',
    height: 'var(--input-height)',
    padding: 'var(--input-padding-y) var(--input-padding-x)',
    border: '1px solid var(--input-border)',
    borderRadius: 'var(--input-radius)',
    backgroundColor: 'var(--input-bg)',
    fontSize: 'var(--input-font-size)',
    fontFamily: 'var(--font-sans)',
    color: 'var(--color-text-primary)',
    outline: 'none',
    transition: 'border-color var(--transition-base), box-shadow var(--transition-base)',
  };
}

function focusInput(el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) {
  el.style.borderColor = 'var(--input-border-focus)';
  el.style.boxShadow = 'var(--shadow-focus)';
}

function blurInput(el: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement) {
  el.style.borderColor = 'var(--input-border)';
  el.style.boxShadow = 'none';
}

function LinkiTip({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 'var(--space-2)',
        padding: 'var(--space-2) var(--space-3)',
        borderRadius: 'var(--radius-input)',
        backgroundColor: 'var(--color-linki-bg)',
        border: '1px solid var(--color-linki-border)',
        color: 'var(--color-linki-text)',
        fontFamily: 'var(--font-sans)',
        fontSize: 'var(--text-sm)',
        fontWeight: 'var(--font-weight-medium)',
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '20px',
          height: '20px',
          borderRadius: 'var(--radius-full)',
          backgroundColor: 'var(--color-brand-teal)',
          color: 'var(--color-text-inverse)',
          flexShrink: 0,
        }}
      >
        <IconSpark size={12} />
      </span>
      {children}
    </div>
  );
}

/* ── Icons ── */

function IconCheck({ size = 16 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>; }
function IconSave({ size = 16 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" /></svg>; }
function IconSpark({ size = 12 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l2.39 6.96L21 11l-6.61 2.04L12 20l-2.39-6.96L3 11l6.61-2.04L12 2z" /></svg>; }
function IconUsers({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></svg>; }
function IconBriefcase({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>; }
function IconMegaphone({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 11v2a2 2 0 0 0 2 2h1l4 4V5L6 9H5a2 2 0 0 0-2 2z" /><path d="M14 5.5v13" /><path d="M18 8a4 4 0 0 1 0 8" /></svg>; }
function IconHandshake({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 17l-2 2a1.4 1.4 0 0 1-2-2l2-2" /><path d="M14 14l-3 3a1.4 1.4 0 1 1-2-2l3-3" /><path d="M16 11l-5 5a1.4 1.4 0 1 1-2-2l5-5" /><path d="M21 3l-2 6a3 3 0 0 1-2 2l-5 5" /><path d="M3 21l2-6a3 3 0 0 1 2-2l5-5" /></svg>; }
function IconCalendar({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></svg>; }
function IconNewspaper({ size = 18 }) { return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h13a2 2 0 0 1 2 2v13a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4z" /><path d="M19 8h2v11a2 2 0 0 1-2 2" /><line x1="8" y1="8" x2="15" y2="8" /><line x1="8" y1="12" x2="15" y2="12" /><line x1="8" y1="16" x2="12" y2="16" /></svg>; }
